import React from 'react';
import { X, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';

const CartDrawer = () => {
  const {
    isCartOpen,
    closeCart,
    cartItems,
    cartTotal,
    cartCount,
    updateQuantity,
    removeFromCart,
  } = useCart();

  return (
    <Sheet open={isCartOpen} onOpenChange={closeCart}>
      <SheetContent className="w-full sm:max-w-md flex flex-col bg-white">
        <SheetHeader className="border-b border-cream-200 pb-4">
          <SheetTitle className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-rose-500" />
            </div>
            <span className="text-warmBrown-900 font-display">Your Cart ({cartCount})</span>
          </SheetTitle>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <div className="w-20 h-20 bg-cream-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="w-8 h-8 text-warmBrown-400" />
            </div>
            <h3 className="text-lg font-semibold text-warmBrown-800 mb-2">Your cart is empty</h3>
            <p className="text-warmBrown-500 mb-6">Add items to get started</p>
            <button
              onClick={closeCart}
              className="px-6 py-3 bg-warmBrown-800 hover:bg-warmBrown-900 text-white font-semibold rounded-2xl transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-4 p-3 bg-cream-50 rounded-2xl border border-cream-200"
                >
                  {/* Image */}
                  <div className="w-20 h-20 bg-white rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-warmBrown-800 text-sm mb-1 line-clamp-1">
                      {item.name}
                    </h4>
                    <p className="text-rose-600 font-bold text-base">
                      ${item.price.toFixed(2)}
                    </p>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center gap-1 bg-white rounded-xl border border-cream-200">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1.5 text-warmBrown-500 hover:text-rose-600 transition-colors disabled:opacity-30"
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-warmBrown-800">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1.5 text-warmBrown-500 hover:text-rose-600 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-1.5 text-warmBrown-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-cream-200 pt-4 space-y-4">
              {/* Subtotal */}
              <div className="flex items-center justify-between">
                <span className="text-warmBrown-600 font-medium">Subtotal</span>
                <span className="text-2xl font-display font-bold text-warmBrown-900">
                  ${cartTotal.toFixed(2)}
                </span>
              </div>

              {/* Free Shipping Note */}
              {cartTotal < 99 && (
                <p className="text-sm text-warmBrown-500 text-center bg-cream-50 py-2 rounded-xl">
                  Add <span className="font-semibold text-rose-600">${(99 - cartTotal).toFixed(2)}</span> more for free shipping!
                </p>
              )}
              {cartTotal >= 99 && (
                <p className="text-sm text-sage-700 text-center bg-sage-50 py-2 rounded-xl font-medium">
                  ✓ You qualify for free shipping!
                </p>
              )}

              {/* Checkout Button */}
              <Link
                to="/checkout"
                onClick={closeCart}
                className="flex items-center justify-center gap-2 w-full py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20 group"
              >
                Proceed to Checkout
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>

              {/* Continue Shopping */}
              <button
                onClick={closeCart}
                className="w-full py-3 text-warmBrown-600 hover:text-rose-600 font-medium transition-colors text-sm"
              >
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
